import { Button, Typography } from "@mui/material";
import Latex from "react-latex-next";
import CartPole from "./cartpole";

interface ContrlProps {
  setKpTheta: React.Dispatch<React.SetStateAction<number>>;
  setKpX: React.Dispatch<React.SetStateAction<number>>;
  setKdTheta: React.Dispatch<React.SetStateAction<number>>;
  setKdX: React.Dispatch<React.SetStateAction<number>>;
  setKiX: React.Dispatch<React.SetStateAction<number>>;
  setXOffset: React.Dispatch<React.SetStateAction<number>>;
  cartPole: CartPole;
  reset: () => void;
}

export default function Controls({
  setKpTheta,
  setKdTheta,
  cartPole,
  reset,
  setKpX,
  setKdX,
  setKiX,
  setXOffset,
}: ContrlProps) {
  return (
    <div className="controls">
      <div>
        <Typography variant="h5" style={{ paddingBottom: 20 }}>
          PID Control with Cart-Pole
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          I will introduce PID control with a Cart-Pole example on this page.
          The Cart-Pole is a very simple robot. It consists of a cart on a 1D
          track with a rod attached at the center, which can swing around a
          pivot. The goal is to balance the pole upright. Here’s an interactive
          tool that tries to manually balance the pole by moving the cart left
          and right.
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          I will not describe the dynamics of the Cart-Pole here. If you are
          interested in learning about it, you can check{" "}
          <a href={"https://underactuated.mit.edu/acrobot.html"}>this link</a>.
        </Typography>
        <img
          width={400}
          src="https://firebasestorage.googleapis.com/v0/b/vis-ro.appspot.com/o/CartPole.001.jpeg?alt=media&token=e037fc8f-ef26-431f-9596-4c8625047e77"
          alt={"Configuration of Cart-Pole System"}
        />
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          Here is a static image of the Cart-Pole system. Our goal is to keep
          the angle
          <Latex>$\theta = 0$</Latex> at all times using PID control theory. As
          you can see, the Cart-Pole is initially unstable.
        </Typography>
        <Typography variant="h5" gutterBottom sx={{ marginTop: 5 }}>
          P (Proportional)
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          We try to apply a force that is proportional to{" "}
          <Latex>$\theta$</Latex>
          <Latex>$$F_x = k^\theta_p \cdot \theta$$</Latex>
          Tuning <Latex>$k^\theta_p$</Latex> requires experiment. Here, we set{" "}
          <Latex>$k^\theta_p = 40$</Latex> initially. You can click the button
          below to see the result.
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            reset();
            setKpTheta(40);
            cartPole.restart();
          }}
          style={{ margin: 20 }}
        >
          Try P Control for Angle
        </Button>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          Now, you can see the Cart-Pole oscillate at{" "}
          <Latex>$\theta = 0$</Latex>. If you observe for a long time, you may
          notice something strange: the Cart-Pole will continuously move to the
          right or left. To fix this, we apply a force that is proportional to{" "}
          <Latex>$x$</Latex>.
          <Latex>$$F_x = k^\theta_p \cdot \theta + k^x_p \cdot x$$</Latex>
          We set <Latex>$k^x_p = 0.1$</Latex> here. Click the button below to
          see the result.
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            reset();
            setKpTheta(40);
            setKpX(0.1);
            cartPole.restart();
          }}
          style={{ margin: 20 }}
        >
          Try P Control for Position
        </Button>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          Now, you will see the Cart-Pole oscillate around the origin point.
        </Typography>
        <Typography variant="h5" gutterBottom sx={{ marginTop: 5 }}>
          D (Derivative)
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          After using P control, you may find the oscillation to be annoying. Is
          there any method to make it stable without oscillation? The answer is
          to apply a force proportional to the speed of the angle and position.
          <Latex>{`$$F_x = k^\\theta_p \\cdot \\theta + k^\\theta_d \\cdot \\dot{\\theta} + k^x_p \\cdot x + k^x_d \\cdot \\dot{x}$$`}</Latex>
          We set <Latex>$k^\theta_d = 5$, $k^x_d = 0.5$</Latex> and you can
          click the button below to see the result.
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            reset();
            setKpTheta(40);
            setKpX(0.1);
            setKdTheta(5);
            setKdX(0.5);
            cartPole.restart();
          }}
          style={{ margin: 20 }}
        >
          Try PD Control
        </Button>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          You can see that the Cart-Pole is now stable at the origin!
          Congratulations!
        </Typography>
        <Typography variant="h5" gutterBottom sx={{ marginTop: 5 }}>
          I (Integral)
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          Now, let's assume there is some wind constantly pushing the Cart-Pole
          with a position offset <Latex>$x_o$</Latex> every second.
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            reset();
            setKpTheta(40);
            setKpX(0.1);
            setKdTheta(5);
            setKdX(0.5);
            setXOffset(0.2);
            cartPole.restart();
          }}
          style={{ margin: 20 }}
        >
          Add X Offset with PD Control
        </Button>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          You will find that it can still be stable, but not at the origin. To
          address this problem, we have to introduce Integral (I) Control, which
          involves adding force in proportion to the integral of{" "}
          <Latex>$x$</Latex>.
          <Latex>{`$$F_x = k^\\theta_p \\cdot \\theta + k^\\theta_d \\cdot \\dot{\\theta} + k^x_p \\cdot x + k^x_d \\cdot \\dot{x} + k^x_i \\cdot \\int x$$`}</Latex>
          We integrate <Latex>$x$</Latex> for half second and set{" "}
          <Latex>$k^x_i = 0.01$</Latex>.
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            reset();
            setKpTheta(40);
            setKpX(0.1);
            setKdTheta(5);
            setKdX(0.5);
            setXOffset(0.2);
            setKiX(0.01);
            cartPole.restart();
          }}
          style={{ margin: 20 }}
        >
          Try PID Control
        </Button>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          Now, you will find the Cart-Pole is stable at the origin again.
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          Congratulations! Now you've learned how to use PID theory to control
          the Cart-Pole system. Note that the parameters of PID are tuned
          through experimentation. You are free to change the parameters, but it
          might be challenging to achieve stability. To overcome this problem,
          we will introduce more control theory to manage the Cart-Pole system
          in the future.
        </Typography>
      </div>
    </div>
  );
}
